import { useState } from 'react'
import type { ExternalHealth } from '@console/types'
import {
  AsOfLine,
  Button,
  DataProvenanceLine,
  DegradationBanner,
  DisclosureSection,
  Fingerprint,
  HairlineRule,
  Input,
  MetricCard,
  Modal,
  QueueItem,
  Select,
  Skeleton,
  Stamp,
  Table,
  Toast,
} from '../ui/index.js'

const HEALTHY: ExternalHealth = {
  zoho: { status: 'ok', detail: null },
  llmProvider: { status: 'ok', detail: null },
  cadence: { status: 'ok', detail: null },
  provenance: {
    host: 'cadence.internal',
    tenantId: '7da726d1-d146-41d0-a5ec-bcda824c302e',
    tenantLabel: null,
    source: 'configured',
    matchesSession: true,
  },
  asOf: new Date().toISOString(),
}

const ZOHO_DEGRADED: ExternalHealth = {
  ...HEALTHY,
  zoho: { status: 'unreachable', detail: null },
}

interface SampleRow {
  id: string
  reference: string
  verdict: string
}

const SAMPLE_ROWS: SampleRow[] = [
  { id: '1', reference: 'QT-000151', verdict: 'confirmed' },
  { id: '2', reference: 'QT-000148', verdict: 'needs you' },
  { id: '3', reference: 'Run 13d63af4', verdict: 'refused' },
]

/** Stands in for a real handler on this dev-only page — logs rather than doing nothing. */
function demoAction(label: string): void {
  console.info(`[kitchen-sink] ${label} clicked`)
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: '2.5rem' }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem' }}>{title}</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-start' }}>
        {children}
      </div>
    </section>
  )
}

/**
 * Dev-only — never registered in a production build (see `App.tsx`, gated
 * on `import.meta.env.DEV`). Two things live here: a gallery of each
 * primitive's own variants, and a worked demonstration of the four surface
 * states from addendum §A (empty / error / degraded / loading) using them
 * together, which is what Phase 3's exit criteria actually asks for — not
 * that every primitive individually has all four, but that the pattern is
 * demonstrably buildable from what exists.
 */
export function KitchenSinkPage() {
  const [modalOpen, setModalOpen] = useState(false)

  return (
    <main style={{ padding: '2rem', maxWidth: '64rem' }}>
      <h1 style={{ fontFamily: 'var(--font-display)' }}>Kitchen sink</h1>
      <p style={{ color: 'var(--muted)' }}>
        Every primitive in `src/ui/`, plus the four surface states worked through together.
        Dev-only.
      </p>

      <HairlineRule strong />

      <Section title="Stamp">
        <Stamp variant="ok" />
        <Stamp variant="warn" />
        <Stamp variant="stop" />
        <Stamp variant="you" />
      </Section>

      <Section title="Button">
        <Button variant="primary">Primary</Button>
        <Button variant="secondary">Secondary</Button>
        <Button variant="danger">Danger</Button>
        <Button variant="primary" disabled>
          Disabled
        </Button>
        <Button variant="primary" loading>
          Loading
        </Button>
      </Section>

      <Section title="Input">
        <Input label="Email" placeholder="you@example.com" />
        <Input label="Email" defaultValue="not-an-email" error="Enter a valid email address." />
      </Section>

      <Section title="Select">
        <Select
          label="Verdict"
          options={[
            { value: 'all', label: 'All' },
            // Deliberately NOT verdict labels. This page demonstrates the
            // Select control, and using real verdict wording here would put a
            // served label in a component — the exact grep CON-003 turns on.
            { value: 'alpha', label: 'First option' },
            { value: 'beta', label: 'Second option' },
          ]}
        />
      </Section>

      <Section title="HairlineRule">
        <div style={{ width: '12rem' }}>
          <HairlineRule />
        </div>
        <div style={{ width: '12rem' }}>
          <HairlineRule strong />
        </div>
      </Section>

      <Section title="DisclosureSection">
        <div style={{ width: '20rem' }}>
          <DisclosureSection title="The receipt">
            <p>Contents shown when expanded.</p>
          </DisclosureSection>
        </div>
      </Section>

      <Section title="Table">
        <div style={{ width: '100%' }}>
          <Table
            columns={[
              { key: 'reference', header: 'Reference', render: (r: SampleRow) => r.reference },
              { key: 'verdict', header: 'Verdict', render: (r: SampleRow) => r.verdict },
            ]}
            rows={SAMPLE_ROWS}
            rowKey={(r) => r.id}
          />
        </div>
      </Section>

      <Section title="Modal">
        <Button
          onClick={() => {
            setModalOpen(true)
          }}
        >
          Open modal
        </Button>
        <Modal
          open={modalOpen}
          title="Example dialog"
          onClose={() => {
            setModalOpen(false)
          }}
        >
          <p>Native &lt;dialog&gt; — focus trap and Escape-to-close for free.</p>
          <Button
            onClick={() => {
              setModalOpen(false)
            }}
          >
            Close
          </Button>
        </Modal>
      </Section>

      <Section title="Toast">
        <Toast variant="info">Informational message.</Toast>
        <Toast variant="ok">Saved.</Toast>
        <Toast variant="error">Something went wrong.</Toast>
      </Section>

      <Section title="Skeleton">
        <div style={{ width: '12rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Skeleton width="60%" />
          <Skeleton />
          <Skeleton width="80%" />
        </div>
      </Section>

      <Section title="AsOfLine">
        <AsOfLine
          asOf={new Date().toISOString()}
          onRefresh={() => {
            demoAction('AsOfLine refresh')
          }}
        />
        <AsOfLine
          asOf={new Date(Date.now() - 12 * 60_000).toISOString()}
          onRefresh={() => {
            demoAction('AsOfLine refresh (stale)')
          }}
        />
      </Section>

      <Section title="DegradationBanner">
        <div style={{ width: '100%' }}>
          <DegradationBanner health={ZOHO_DEGRADED} />
        </div>
      </Section>

      {/* All three states that render. The fourth — `reported` and matching —
          is the one that renders nothing, and is the only silence this
          component is allowed. */}
      <Section title="DataProvenanceLine">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
          <DataProvenanceLine
            provenance={HEALTHY.provenance}
            sessionTenantName="Northwind Trading (demo)"
          />
          <DataProvenanceLine
            provenance={{
              ...HEALTHY.provenance,
              tenantId: null,
              source: 'unknown',
              matchesSession: null,
            }}
            sessionTenantName="Northwind Trading (demo)"
          />
          <DataProvenanceLine
            provenance={{ ...HEALTHY.provenance, matchesSession: false }}
            sessionTenantName="Northwind Trading (demo)"
          />
        </div>
      </Section>

      <Section title="MetricCard">
        <MetricCard value="247" label="Runs (7 day)" detail="↑ 12 vs last week" />
        <MetricCard value="91%" label="Autonomy" detail="25/48 autonomous" />
      </Section>

      <Section title="QueueItem">
        <div style={{ width: '100%' }}>
          <QueueItem
            stamp="warn"
            title="QT-000148 · Approval needed"
            detail="Al Noor Trading · 3 lines · received 12 minutes ago"
            actionLabel="Review"
            onAction={() => {
              demoAction('QueueItem review')
            }}
          />
        </div>
      </Section>

      <Section title="Fingerprint">
        <Fingerprint value="b8ef4a1234567890abcdef1234567890abcdef1234567890abcdef12343812" />
      </Section>

      <HairlineRule strong />

      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem' }}>
        The four surface states (addendum §A)
      </h2>

      <Section title="Loading">
        <div style={{ width: '20rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Skeleton width="40%" />
          <Skeleton />
          <Skeleton />
        </div>
      </Section>

      <Section title="Empty">
        <p style={{ color: 'var(--muted)' }}>Nothing needs you right now. Runs are flowing.</p>
      </Section>

      <Section title="Error">
        <Toast variant="error">
          Could not reach the console server. Tried at 14:31 UTC. [Retry]
        </Toast>
      </Section>

      <Section title="Degraded">
        <div style={{ width: '100%' }}>
          <DegradationBanner health={ZOHO_DEGRADED} />
          <p style={{ color: 'var(--muted)' }}>
            The surface's own data still renders normally beneath the banner — degradation is
            orthogonal to the surface's own loading/empty/error state.
          </p>
        </div>
      </Section>
    </main>
  )
}
